import React from 'react';
import { formatTimeRange } from '@features/appointments/utils/dateTimeFormatters';
import { ReadinessIndicator } from '@features/eligibility-checks/components/ReadinessIndicator';
import StickyNote2Outlined from '@mui/icons-material/StickyNote2Outlined';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import { Box, type SxProps } from '@mui/material';

import { getFixtureProvider } from '../../../../../fixtures';
import { getExtendedColor, providerInitials } from '../../../../../theme/extendedPalette';
import { getVisitStatusDisplay } from '../../../../../theme/visitStatus';
import type { CalendarEvent } from '../../../types';

import {
  ConfirmedIcon,
  EventWrapper,
  TimeLabel,
  TitleRow,
  TitleText,
  TitleWrapper,
} from './BaseEvent.styles';

/**
 * Props for BaseEvent component
 */
export interface BaseEventProps {
  event: CalendarEvent;
  title: React.ReactNode;
  viewMode?: string;
  /** Callback ref or ref object — used by Floating UI to anchor the popover. */
  eventRef?: React.Ref<HTMLDivElement> | ((node: HTMLDivElement | null) => void);
  /** Spread on the EventWrapper — typically `getReferenceProps()` from Floating UI. */
  referenceProps?: React.HTMLAttributes<HTMLDivElement>;
  sx?: SxProps;
}

export const BaseEvent: React.FC<BaseEventProps> = ({
  event,
  title,
  viewMode,
  eventRef,
  referenceProps,
  sx,
}) => {
  const readiness = event.resource.block_state?.readiness ?? null;
  const provider = getFixtureProvider(event.resource.provider_id);
  const visitStatus = getVisitStatusDisplay(event.resource.visit_status);
  const VisitStatusIcon = visitStatus.icon;

  return (
    <EventWrapper ref={eventRef} sx={sx} {...referenceProps}>
      <TitleWrapper>
        <TitleRow>
          {provider && (
            <Box
              component="span"
              aria-label={`Provider: ${provider.name}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 16,
                minWidth: 22,
                px: 0.5,
                borderRadius: '4px',
                bgcolor: getExtendedColor(provider.color),
                color: '#fff',
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.02em',
                flexShrink: 0,
              }}
            >
              {providerInitials(provider.name)}
            </Box>
          )}
          <TitleText>{title}</TitleText>
        </TitleRow>

        {/* Time label - hidden in month view to reduce event height */}
        {viewMode !== 'month' && <TimeLabel>{formatTimeRange(event.start, event.end)}</TimeLabel>}
      </TitleWrapper>

      {/* Right cluster — status indicators.
          Default (medium/large events): 2-column grid, wraps to 2 rows when 3+
          icons are present. Readiness chip is pinned to (row 1, col 2) so it's
          always the top-right.
          Small events / month view (no container query context): collapses to
          a single flex row; readiness gets `order: 1` to stay rightmost. */}
      <Box
        sx={{
          alignSelf: 'flex-start',
          display: 'grid',
          gridTemplateColumns: 'auto auto',
          gridAutoFlow: 'row',
          gap: '4px',
          flexShrink: 0,
          '@container (height < 45px)': {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          },
        }}
      >
        {visitStatus.showIconOnCard && (
          <VisitStatusIcon
            aria-label={visitStatus.label}
            sx={{ fontSize: 14, color: visitStatus.color, opacity: 0.85 }}
          />
        )}
        {event.resource.telehealth === true && (
          <VideocamOutlined sx={{ fontSize: 14, opacity: 0.7 }} />
        )}
        {event.resource.notes && (
          <StickyNote2Outlined sx={{ fontSize: 14, opacity: 0.7 }} />
        )}
        {event.resource.block_state?.qualifiers?.confirmed && <ConfirmedIcon />}
        {readiness && (
          <Box
            sx={{
              // 2x2 grid: pin to row 1 col 2 (top-right)
              gridColumn: 2,
              gridRow: 1,
              // Single-row flex (small / month view): push to end
              order: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ReadinessIndicator readiness={readiness} />
          </Box>
        )}
      </Box>
    </EventWrapper>
  );
};
